import React, { FC, Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

import { renderOptions } from '../components/form/selectOptions';
import Modal from '../components/overlay/Modal';
import TranslationForm from '../components/translations/TranslationForm';
import Row from '../components/layout/Row';
import ColumnItem from '../components/layout/ColumnItem';
import Spacer from '../components/layout/Spacer';
import Badge from '../components/header/Badge';
import { useAppDispatch, useAppSelector } from '../redux/redux.hooks';
import {
  getTranslations,
  resetUpdateTranslationStatus,
  setCurrentLanguage,
  setInitialCategoryTranslation,
  setInitialDefaultTranslation,
  setInitialWidgetTranslation,
} from '../reducers/translations.reducer';
import Loading from '../components/loading/Loading';
import { useToast } from '../components/toast/ToastProvider';
import {
  createProductCategory,
  getEasysizeCategories,
  resetCategoryData,
} from '../reducers/category.reducer';
import {
  CategoryGroup,
  CreateCategoryRequest,
} from '../requests/create.category.request';

type WidgetTranslationsProps = {};

type TranslationPill = {
  name: string;
  key: string;
};

type TranslationType = 'default' | 'widget' | 'category';

const WidgetTranslations: FC<WidgetTranslationsProps> = () => {
  const params = useParams();
  const { id } = params;
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalName, setModalName] = useState<string>('');
  const [modalKey, setModalKey] = useState<string>('');
  const [translationType, setTranslationType] =
    useState<TranslationType>('default');
  const toast = useToast();
  const [categoryPills, setCategoryPills] = useState<TranslationPill[]>([]);

  const [categoryName, setCategoryName] = useState<string>('');
  const [categoryGroup, setCategoryGroup] = useState<string>('basic');
  const [easysizeCategory, setEasysizeCategory] = useState<string>('');

  const [easysizeCategoryOptions, setEasysizeCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [categoryNameHasError, setCategoryNameHasError] =
    useState<boolean>(false);

  const categoryNameError = 'The category name cannot be empty';

  const dispatch = useAppDispatch();
  const {
    status: category_status,
    data: easysize_categories,
    createCategoryStatus,
  } = useAppSelector((state) => state.categoryReducer);
  const { status, updateStatus, supportedCategories } = useAppSelector(
    (state) => state.translationsReducer
  );

  const ctaPills: TranslationPill[] = [
    { name: 'Add to cart', key: 'WIDGET_ADD_TO_CART' },
    { name: 'Continue shopping', key: 'WIDGET_CONTINUE_SHOPPING' },
  ];

  const resultPills: TranslationPill[] = [
    { name: 'Out of stock', key: 'WIDGET_RESULT_NOSTOCK_HEADER' },
    { name: "Can't find size", key: 'WIDGET_RESULT_NOTFOUND_HEADER' },
    { name: 'Size found title', key: 'WIDGET_RESULT_TITLE' },
    { name: 'Size found subtitle', key: 'WIDGET_RESULT_SUBTITLE' },
  ];

  const fitquizCtaPills: TranslationPill[] = [
    { name: 'Not sure about the size', key: 'BUTTON_N' },
    { name: 'We recommend', key: 'BUTTON_R' },
  ];

  const topPills: TranslationPill[] = [
    { name: 'Top size', key: 'GIVEN_SIZE__TOPS' },
    { name: 'Your top size', key: 'WIDGET_RESULT_YOUR_TOP_SIZE' },
    { name: 'Belly type', key: 'BELLY_TYPE' },
    { name: 'Chest type', key: 'CHEST_TYPE' },
    { name: 'Chest fit issues', key: 'CHEST_CHALLENGE' },
    { name: 'Torso length', key: 'TORSO_LENGTH' },
  ];

  const bottomPills: TranslationPill[] = [
    { name: 'Bottom size', key: 'GIVEN_SIZE__BOTTOMS' },
    { name: 'Hip type', key: 'HIPS_TYPE' },
    { name: 'Bottom length', key: 'BOTTOM_LENGTH' },
  ];

  const setsPills: TranslationPill[] = [
    { name: 'Bottom size', key: 'SECONDARY_GIVEN_SIZE' },
    { name: 'Bottom fit', key: 'SECONDARY_PREFERRED_FIT' },
  ];

  const footPills: TranslationPill[] = [
    { name: 'Foot size', key: 'GIVEN_SIZE__FOOTWEAR' },
    { name: 'Foot shape', key: 'FOOT_SHAPE' },
    { name: 'Foot shape issues', key: 'FOOT_SHAPE_CHALLENGE' },
    { name: 'Foot arch', key: 'FOOT_ARCH' },
    { name: 'Foot width', key: 'FOOT_WIDTH' },
    { name: 'Foot width issues', key: 'FOOT_WIDTH_CHALLENGE' },
    { name: 'Foot length issues', key: 'FOOT_LENGTH_CHALLENGE' },
    { name: 'Foot bunion', key: 'FOOT_BUNION' },
  ];

  const fitPills: TranslationPill[] = [
    { name: 'Preferred fit', key: 'PREFERRED_FIT' },
  ];

  const lengthPills: TranslationPill[] = [
    { name: 'Overall length issues', key: 'OVERALL_LENGTH_CHALLENGE' },
  ];

  const genderPills: TranslationPill[] = [
    { name: 'Choose gender', key: 'PRODUCT_GENDER' },
  ];

  const heightPills: TranslationPill[] = [
    { name: 'Height', key: 'HEIGHT' },
  ];

  const swimwearPills: TranslationPill[] = [
    { name: 'Swimwear', key: 'GIVEN_SIZE__SWIMWEAR' },
  ];

  const sizePills: TranslationPill[] = [
    { name: '2XS', key: '2XS' },
    { name: 'XXS', key: 'XXS' },
    { name: 'XS', key: 'XS' },
    { name: 'S', key: 'S' },
    { name: 'M', key: 'M' },
    { name: 'L', key: 'L' },
    { name: 'XL', key: 'XL' },
    { name: 'XXL', key: 'XXL' },
    { name: 'XXXL', key: 'XXXL' },
    { name: '2XL', key: '2XL' },
    { name: '3XL', key: '3XL' },
    { name: '4XL', key: '4XL' },
  ];

  const miscPills: TranslationPill[] = [
    { name: 'Your fit', key: 'WIDGET_RESULT_YOUR_FIT' },
    { name: 'Your size', key: 'WIDGET_RESULT_YOUR_SIZE' },
    { name: 'Return to shopping', key: 'WIDGET_RESULT_NOTFOUND_CTA' },
    { name: 'Next', key: 'WIDGET_NEXT' },
    { name: 'Back', key: 'WIDGET_PREVIOUS' },
    { name: 'Reset', key: 'WIDGET_RESET' },
    { name: 'Do you always wear this size', key: 'SIZE_DIRECTION' },
    { name: 'Take our fit quiz', key: 'WIDGET_QUESTION_TITLE' },
    { name: 'Widget info', key: 'WIDGET_INFO' },
    { name: 'Size Guide Title', key: 'WIDGET_SIZE_GUIDE_MODAL_TEXT' },
    { name: 'Size Guide CTA', key: 'WIDGET_SIZE_GUIDE_MODAL_CTA' },
  ];

  const es_category_groups = ([] = [
    { label: 'Basic', value: 'basic' },
    { label: 'Ethnic wear', value: 'ethnic' },
    { label: 'Swimwear and underwear', value: 'swim' },
    { label: 'Footwear', value: 'foot' },
    { label: 'Sportswear', value: 'sports' },
  ]);

  const getInitialTranslationByType = (tType: TranslationType, key: string) => {
    switch (tType) {
      case 'category':
        dispatch(setInitialCategoryTranslation(key));
        break;
      case 'widget':
        dispatch(setInitialWidgetTranslation(key));
        break;
      case 'default':
      default:
        dispatch(setInitialDefaultTranslation(key));
        break;
    }
  };

  const categoryNameBlur = () => {
    setCategoryNameHasError(categoryName.length === 0);
  };

  const handleCreateCategory = () => {
    const createCategoryRequest: CreateCategoryRequest = {
      categoryGroup: categoryGroup as CategoryGroup,
      categoryName,
      easysizeCategory,
      shopId: id!,
    };
    dispatch(createProductCategory(createCategoryRequest));
    let categories = [...categoryPills];
    categories.push({
      name: categoryName,
      key: easysizeCategory.toUpperCase(),
    });
    setCategoryPills(categories)
  };

  const renderCreateCategoryModal = () => {
    return (
      <Modal
        open={showCreateCategoryModal}
        onClose={() => setShowCreateCategoryModal(false)}
        heading='Create category'
      >
        <Row>
          <ColumnItem style={{ width: '100%' }}>
            <s-text-field
              id='category-name'
              name='category-name'
              label='Category name'
              value={categoryName}
              onInput={(event) => setCategoryName(event.currentTarget.value)}
              onBlur={categoryNameBlur}
              error={categoryNameHasError ? categoryNameError : ''}
              autocomplete='off'
            />
          </ColumnItem>
        </Row>
        <Spacer height={16} />
        <Row>
          <ColumnItem>
            <s-select
              label='Easysize categories'
              onChange={({ currentTarget: { value } }) => setEasysizeCategory(value)}
              value={easysizeCategory}
            >
              {renderOptions(easysizeCategoryOptions)}
            </s-select>
          </ColumnItem>
          <ColumnItem>
            <s-select
              label='Category group'
              onChange={({ currentTarget: { value } }) => setCategoryGroup(value)}
              value={categoryGroup}
            >
              {renderOptions(es_category_groups)}
            </s-select>
          </ColumnItem>
        </Row>
        <Spacer height={16} />
        <Row>
          <ColumnItem style={{ width: '100%' }}>
            <ButtonContainer>
              <s-button
                variant='primary'
                onClick={handleCreateCategory}
                disabled={
                  categoryName?.trim() === '' ||
                  easysizeCategory?.trim() === '' ||
                  categoryGroup?.trim() === ''
                }
                loading={createCategoryStatus === 'loading'}
              >
                Create Category
              </s-button>
            </ButtonContainer>
          </ColumnItem>
        </Row>
      </Modal>
    );
  };

  const renderShowModal = (key: string, pillName: string) => {
    return (
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        heading={pillName}
        size='large'
      >
        <TranslationForm
          shopId={id!}
          onLanguageChange={(locale: string) => {
            const language = locale.toLowerCase().includes('spanish')
              ? 'spanish; castilian'
              : locale;
            dispatch(setCurrentLanguage(language));
            getInitialTranslationByType(translationType, key);
          }}
        />
      </Modal>
    );
  };

  const renderSection = (pills: TranslationPill[], title: string) => {
    return (
      <SectionContainer>
        <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <s-heading fontSize='large'>{title}</s-heading>
          {title.toLowerCase() === 'categories' && (
            <>
              <Spacer />
              <s-button onClick={() => setShowCreateCategoryModal(true)}>
                Create category
              </s-button>
            </>
          )}
        </Row>
        <Spacer height={24} />
        <Row>
          <FlatList>
            {pills.map(({ key, name }, index) => (
              <Fragment key={`i-${index}`}>
                <Badge
                  backgroundColor='#f1f1f1'
                  style={{ marginBottom: 12 }}
                  onClick={() => {
                    let ttype: TranslationType;
                    if (title.toLowerCase() === 'categories') {
                      ttype = 'category';
                      setTranslationType('category');
                    } else if (title.toLowerCase() === 'fitquiz cta') {
                      ttype = 'widget';
                      setTranslationType('widget');
                    } else {
                      ttype = 'default';
                      setTranslationType('default');
                    }
                    getInitialTranslationByType(ttype, key);
                    setShowModal(true);
                    setModalName(name);
                    setModalKey(key);
                  }}
                >
                  <BadgeText>{name.toLowerCase()}</BadgeText>
                </Badge>
                <Spacer width={12} />
              </Fragment>
            ))}
          </FlatList>
        </Row>
      </SectionContainer>
    );
  };

  useEffect(() => {
    if (createCategoryStatus === 'successful') {
      setShowCreateCategoryModal(false);
      dispatch(resetCategoryData());
    }
  }, [dispatch, createCategoryStatus]);

  useEffect(() => {
    setCategoryPills(
      supportedCategories || [
        { name: 'Shirt', key: 'SHIRT' },
        {
          name: 'Blazer',
          key: 'BLAZER',
        },
        {
          name: 'Vests',
          key: 'VESTS',
        },
        {
          name: 'Jacket',
          key: 'JACKET',
        },
        {
          name: 'Cardigan',
          key: 'CARDIGAN',
        },
        {
          name: 'Coat',
          key: 'COAT',
        },
        {
          name: 'Dress',
          key: 'DRESS',
        },
        {
          name: 'Footwear',
          key: 'FOOTWEAR',
        },
        {
          name: 'Jeans',
          key: 'JEANS',
        },
        {
          name: 'Knitwear',
          key: 'KNITWEAR',
        },
        {
          name: 'Outerwear',
          key: 'OUTERWEAR',
        },
        {
          name: 'Pants',
          key: 'PANTS',
        },
        {
          name: 'Shoes',
          key: 'SHOES',
        },
        {
          name: 'Shorts',
          key: 'SHORTS',
        },
        {
          name: 'Skirt',
          key: 'SKIRT',
        },
        {
          name: 'Sweater',
          key: 'SWEATER',
        },
        {
          name: 'Sweatshirt',
          key: 'SWEATSHIRT',
        },
        {
          name: 'Tops',
          key: 'TOP',
        },
        {
          name: 'T-Shirts & tops',
          key: 'T-SHIRTS & TOPS',
        },
        {
          name: 'Underwear tops',
          key: 'UNDERWEAR TOPS',
        },
        {
          name: 'Underwear bottoms',
          key: 'UNDERWEAR BOTTOMS',
        },
        {
          name: 'Jumpsuit',
          key: 'JUMPSUIT',
        },
        {
          name: 'Bodysuit',
          key: 'BODYSUIT',
        },
      ]
    );
  }, [supportedCategories]);

  useEffect(() => {
    dispatch(getTranslations(id!));
  }, [dispatch, id]);

  useEffect(() => {
    if (updateStatus === 'successful') {
      setShowModal(false);
      toast.show('Translations updated successfully 🕺');
      dispatch(resetUpdateTranslationStatus());
    } else if (updateStatus === 'error') {
      toast.show('Failed to update the translations 😿', { isError: true });
      dispatch(resetUpdateTranslationStatus());
    }
  }, [dispatch, updateStatus, toast]);

  useEffect(() => {
    let es_categories: {label: string, value: string}[] = [];
    easysize_categories.map((c) => {
      es_categories.push({
        label: c.easysize_category,
        value: c.easysize_category,
      });
    });
    setEasysizeCategoryOptions(es_categories);
  }, [easysize_categories]);

  // Get Easysize categories
  useEffect(() => {
    dispatch(getEasysizeCategories());
  }, [dispatch]);

  const renderView = () => {
    if (status !== 'successful' || category_status !== 'successful') {
      return <Loading loadingMessage='Gathering translations' />;
    }
    return (
      <Container>
        {renderSection(ctaPills, 'Cart CTA')}
        <Spacer height={16} />
        {renderSection(fitquizCtaPills, 'Fitquiz CTA')}
        <Spacer height={16} />
        {renderSection(resultPills, 'Result')}
        <Spacer height={16} />
        {renderSection(topPills, 'Tops')}
        <Spacer height={16} />
        {renderSection(bottomPills, 'Bottoms')}
        <Spacer height={16} />
        {renderSection(setsPills, 'Sets')}
        <Spacer height={16} />
        {renderSection(swimwearPills, 'Swimwear')}
        <Spacer height={16} />
        {renderSection(footPills, 'Foot')}
        <Spacer height={16} />
        {renderSection(fitPills, 'Fit')}
        <Spacer height={16} />
        {renderSection(lengthPills, 'Length')}
        <Spacer height={16} />
        {renderSection(genderPills, 'Gender')}
        <Spacer height={16} />
        {renderSection(heightPills, 'Height')}
        <Spacer height={16} />
        {renderSection(sizePills, 'Sizes')}
        <Spacer height={16} />
        {renderSection(categoryPills, 'Categories')}
        <Spacer height={16} />
        {renderSection(miscPills, 'Misc')}
        {renderShowModal(modalKey, modalName)}
        {renderCreateCategoryModal()}
      </Container>
    );
  };

  return <>{renderView()}</>;
};

export default WidgetTranslations;

const Container = styled.div`
  width: 90%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 80px;
`;

const FlatList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const BadgeText = styled.span`
  color: #212121;
  text-transform: capitalize;
`;

const SectionContainer = styled.div`
  width: 100%
  min-height: 120px;
  background: #fafafa;
  border-radius: 12px;
  box-shadow: 0 0 10px #e6e6e6;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
